<template lang="pug">
  #member-center-settings-individual-form
    ContentLoader(:width="700" :height="130" v-if="!memberLoaded")
      rect(x="0", y="0", rx="4", ry="4", width="15%", height="15")
      rect(x="0", y="30", rx="4", ry="4", width="15%", height="15")
      rect(x="35%", y="30", rx="4", ry="4", width="15%", height="15")
      rect(x="70%", y="30", rx="4", ry="4", width="15%", height="15")
      rect(x="0", y="50", rx="4", ry="4", width="30%", height="20")
      rect(x="35%", y="50", rx="4", ry="4", width="30%", height="20")
      rect(x="70%", y="50", rx="4", ry="4", width="30%", height="20")

      rect(x="0", y="90", rx="4", ry="4", width="15%", height="15")
      rect(x="35%", y="90", rx="4", ry="4", width="15%", height="15")
      rect(x="70%", y="90", rx="4", ry="4", width="15%", height="15")
      rect(x="0", y="110", rx="4", ry="4", width="30%", height="20")
      rect(x="35%", y="110", rx="4", ry="4", width="30%", height="20")
      rect(x="70%", y="110", rx="4", ry="4", width="30%", height="20")
    div(v-else)
      BNotification(:active.sync="error" type="is-danger" :duration="3000")
        | {{ errorMessage }}
      WtTitle(card-title) Edit Profile Settings
      .columns.is-8.is-variable.is-multiline
        .column.is-4
          BField(label="First Name")
            BInput(v-model="memberCopy.firstName" placeholder="First Name")
        .column.is-4d
          BField(label="Last Name")
            BInput(v-model="memberCopy.lastName" placeholder="Last Name")
        .column.is-4
          BField(label="Registered Date")
            BDatepicker(
              :editable="$auth.user().isWtAdmin"
              placeholder="Click to select..."
              icon="calendar-today"
              v-model="memberCopy.registeredDate"
              :date-formatter="(date) => this.date(date, 'DATE_FORMAT_SHORT_MONTH').formatted"
              :min-date="date(coreProgramMeta.startDate).dateObject"
              indicators="dots"
              :events="[{ date: date(coreProgramMeta.startDate).dateObject, type: 'is-danger' }]"
            )
              template(v-slot:default)
                .indicator-key.program-indicator-key.has-text-danger •
                  span.has-text-dark.ml-2(
                    @click="memberCopy.registeredDate = date(coreProgramMeta.startDate).dateObject"
                  ) program start date
          p.has-text-danger(v-if="$auth.user().isWtAdmin") * The input is only editable for WT-admins and the date can be edited prior to program start date.
        .column.is-4
          // TODO don't want UniquenessCheck with starting value to trigger validation, v-if is a workaround
          UniquenessCheck(
            v-if="memberLoaded && !_.isEmpty(memberCopy)"
            label="Username"
            entity="memberUsername"
            placeholder="username"
            v-model="memberCopy.username"
            @validName="$event => checkUsernameDisableSave($event)"
            :required="false"
          )
        .column.is-4
          // TODO don't want UniquenessCheck with starting value to trigger validation, v-if is a workaround
          UniquenessCheck(
            v-if="memberLoaded && !_.isEmpty(memberCopy)"
            label="Email Address"
            entity="memberEmail"
            placeholder="name@email.com"
            v-model="memberCopy.email"
            @validName="$event => checkEmailDisableSave($event)"
            :disabled="isConnectedProgram"
            :required="false"
          )
          p.has-text-grey.is-size-7(v-if="isConnectedProgram") Email cannot be changed on a connected program.
        .column.is-4(v-if="coreProgramMeta.requireEmployeeId")
          BField(
            label-for="employee-id"
            :label="coreProgramMeta.employeeIdTitle"
            :placeholder="coreProgramMeta.employeeIdTitle"
          )
            BInput#employee-id(
              v-model="memberCopy.employeeId"
              maxlength="70"
            )
        .column.is-4
          BField(label="Password")
            WtButton.reset-password-button(
              is-text
              color="primary"
              @click.native="resetPassword"
              semibold
              :loading="processingForgotPasswordEmail"
            ) Reset Password
        .column.is-4
          BField(label="Account Status")
            div
              p.has-text-danger.mb-2(v-if="member.failedLoginAttempts > 4 && !processingUnlock")
                | Account locked due to multiple failed login attempts
              p.has-text-warning.mb-2(v-else-if="member.failedLoginAttempts > 4 && processingUnlock")
                | Unlocking account...
              p.has-text-success.mb-2(v-else :style="{ opacity: '0.6' }")
                | Account is not locked
              WtButton.unlock-account-button(
                is-text
                color="primary"
                @click.native="unlockAccount"
                semibold
                :loading="processingUnlock"
                :disabled="!member.failedLoginAttempts || processingUnlock"
              ) Unlock Account
        .column.is-4(v-if="displayOrganizationsDropdown")
          BField(:label="_.get(coreProgramMeta,'organizationsName','Organization')")
            BSelect(v-model="memberCopy.organizationId" placeholder="Select Organization")
              option(:value="0") None
              option(
                v-for="option in memberOrganizations"
                v-if="option.active"
                :key="option.id"
                :value="option.id"
              ) {{ option.name }}

      //- PASSWORD CHANGE SECTION
      WtTitle(card-title) Change Password
      .columns.is-8.is-variable.is-multiline
        .column.is-4
          BField(
            label="New Password"
            :type="{ 'is-danger': errors.has('new password') }"
            :message="errors.first('new password')"
          )
            BInput(
              name="new password"
              type="password"
              v-model="newPassword"
              v-validate="'min:8'"
              ref="new password"
              placeholder="New password"
            )
        .column.is-4
          BField(
            label="Confirm Password"
            :type="{ 'is-danger': errors.has('confirm password') }"
            :message="errors.first('confirm password')"
          )
            BInput(
              name="confirm password"
              type="password"
              v-model="confirmPassword"
              v-validate="newPassword ? 'required|confirmed:new password' : ''"
              data-vv-as="new password"
              placeholder="Confirm new password"
            )

      //- PASSWORD CONTROLS
      .level.is-mobile.password-controls
        .level-left
          .level-item.buttons
            WtButton(
              color="primary"
              size="small"
              bold
              @click.native="changePassword"
              :loading="changingPassword"
              :disabled="!newPassword || errors.has('confirm password') || changingPassword"
            ) CHANGE PASSWORD

      //- CONTROLS
      .level.is-mobile.member-settings-form-controls
        .level-left
          .level-item.buttons
            WtButton(
              color="primary"
              size="small"
              bold
              @click.native="toggleAdmin"
              :loading="processingAdmin"
              :disabled="!memberLoaded || processingAdmin || processingDelete"
            ) {{ adminButtonText }}
            WtButton(
              color="red"
              size="small"
              bold
              @click.native="deleteMember"
              :loading="processingDelete"
              :disabled="!memberLoaded || processingAdmin || processingDelete"
            ) DELETE MEMBER
        .level-right
          .level-item
            WtButton(
              is-text
              color="lightgrey"
              size="medium"
              @click.native="reset"
            ) RESET
          .level-item
            WtButton(
              is-text
              bold
              color="primary"
              size="medium"
              @click.native="save"
              :loading="saveInProgress"
              :disabled="disableSave || errors.any() || saveInProgress"
            ) SAVE
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import { ContentLoader } from "vue-content-loader";
import WtTitle from "@@/WtTitle/WtTitle";
import WtTile from "@@/WtTile/WtTile";
import WtButton from "@@/WtButton/WtButton";
import UniquenessCheck from "@@/WtUniquenessCheckInput/WtUniquenessCheckInput";

export default {
  name: "MemberCenterSettingsIndividualForm",

  components: { WtTitle, WtButton, WtTile, ContentLoader, UniquenessCheck },

  mixins: [dateUtilityMixin],

  props: {
    member: {
      type: Object,
      required: true
    },

    memberCopy: {
      type: Object,
      required: true
    },

    backRouteName: {
      type: String,
      default: "MemberCenter"
    }
  },

  data() {
    return {
      saveInProgress: false,
      disableSave: true,
      error: false,
      errorMessage: null,
      formData: {},
      processingForgotPasswordEmail: false,
      processingAdmin: false,
      processingDelete: false,
      processingUnlock: false,
      newPassword: "",
      confirmPassword: "",
      changingPassword: false,
      memberOrganizations: [],
      memberProgramMeta: null,
      memberProgramIsCoreTier: false
    };
  },

  computed: {
    ...mapGetters("authMember", ["isAdmin"]),
    ...mapGetters("members", ["memberLoaded"]),
    ...mapGetters("program", [
      "coreProgramMeta",
      "programSettingsCopy",
      "programOrganizations",
      "programOrganizationsLoaded",
      "isProTier"
    ]),

    isConnectedProgram() {
      return this.programSettingsCopy.isConnectedProgram;
    },

    adminButtonText() {
      return this.member.programAdmin ? "REVOKE ADMIN" : "MAKE ADMIN";
    },

    organizationsLabel() {
      if (this.memberOrganizations.length && this.memberCopy.organizationId) {
        return this._.find(this.memberOrganizations, {
          id: this.memberCopy.organizationId
        }).name;
      } else {
        return `Select ${this._.get(
          this.coreProgramMeta,
          "organizationsName",
          "Organization"
        )}`;
      }
    },

    displayOrganizationsDropdown() {
      return (
        this.isAdmin &&
        !this.memberProgramIsCoreTier &&
        this.memberOrganizations.length > 0
      );
    }
  },

  watch: {
    "memberCopy.firstName"(newVal) {
      // if new firstName, enable save
      if (newVal !== this.member.firstName && newVal !== "") {
        this.disableSave = false;
        this.formData.firstName = newVal;
      }

      if (newVal === this.member.firstName) {
        this.disableSave = true;

        if (this.formData.hasOwnProperty("firstName")) {
          delete this.formData.firstName;
        }
      }
    },

    "memberCopy.lastName"(newVal) {
      // if new lastName, enable save
      if (newVal !== this.member.lastName && newVal !== "") {
        this.disableSave = false;
        this.formData.lastName = newVal;
      }

      if (newVal === this.member.lastName) {
        this.disableSave = true;

        if (this.formData.hasOwnProperty("lastName")) {
          delete this.formData.lastName;
        }
      }
    },

    "memberCopy.employeeId"(newVal) {
      // if new employeeId, enable save
      if (newVal !== this.member.employeeId && newVal !== "") {
        this.disableSave = false;
        this.formData.employeeId = newVal;
      }

      if (newVal === this.member.employeeId) {
        this.disableSave = true;

        if (this.formData.hasOwnProperty("employeeId")) {
          delete this.formData.employeeId;
        }
      }
    },

    "memberCopy.registeredDate"(newVal) {
      // if new registeredDate, enable save
      let oldDate = this.date(this.member.registeredDate, "DATE_FORMAT_DB")
        .formatted;
      let newDate = this.date(newVal, "DATE_FORMAT_DB").formatted;
      if (newDate !== oldDate && newVal !== "") {
        this.disableSave = false;
        this.formData.registeredDate = newVal;
      }

      if (newDate === oldDate) {
        this.disableSave = true;

        if (this.formData.hasOwnProperty("registeredDate")) {
          delete this.formData.registeredDate;
        }
      }
    },

    "memberCopy.organizationId"(newVal) {
      // if new organizationId, enable save
      if (newVal !== this.member.organizationId && newVal !== "") {
        this.disableSave = false;
        this.formData.organizationId = newVal;
      }

      if (newVal === this.member.organizationId) {
        this.disableSave = true;

        if (this.formData.hasOwnProperty("registeredDate")) {
          delete this.formData.organizationId;
        }
      }
    },

    "memberCopy.email"(newVal) {
      if (this.isConnectedProgram) return;
      if (newVal !== this.member.email && newVal !== "") {
        this.disableSave = false;
        this.formData.email = newVal;
      }
      if (newVal === this.member.email) {
        this.disableSave = true;
        delete this.formData.email;
      }
    },

    member: {
      handler() {
        this.loadMemberOrganizations();
      },
      deep: true
    }
  },

  async mounted() {
    this.loadMemberOrganizations();
  },

  methods: {
    ...mapActions("members", ["updateMember", "delete"]),
    ...mapActions("superAdmin", ["resetMemberPassword"]),
    ...mapActions(["snackBar"]),
    ...mapActions("registration", ["forgotPassword"]),
    ...mapActions("program", ["makeAdmin", "revokeAdmin"]),

    async loadMemberOrganizations() {
      if (!this.member || !this.member.programId || !this.isAdmin) {
        return;
      }

      try {
        const programResponse = await this.$axios.get(
          `programs/${this.member.programId}`
        );
        this.memberProgramMeta = programResponse.data.data;
      } catch {
        this.memberProgramMeta = null;
      }

      try {
        const orgsResponse = await this.$axios.get(
          `programs/${this.member.programId}/organizations`
        );
        this.memberOrganizations = orgsResponse.data.data;
        this.memberProgramIsCoreTier = this._.get(
          orgsResponse,
          "data.isCoreTier",
          false
        );
      } catch {
        this.memberOrganizations = [];
        this.memberProgramIsCoreTier = false;
      }
    },

    async save() {
      this.saveInProgress = true;
      // TODO refactor updateMember to throw in catch so we can use try/catch here
      const response = await this.updateMember({
        memberId: this.member.id,
        formData: this.formData
      });

      if (response.error) {
        const fields = response.error.fields;
        const firstErrorKey = Object.keys(fields)[0];
        this.errorMessage = fields[firstErrorKey][0];
        this.error = true;
      } else {
        await this.snackBar({
          message: `${this.member.username}s settings have been updated.`
        });
        this.disableSave = true;
      }

      this.saveInProgress = false;
    },

    reset() {
      this.$set(this.memberCopy, "firstName", this.member.firstName);
      this.$set(this.memberCopy, "lastName", this.member.lastName);
      this.$set(this.memberCopy, "username", this.member.username);
      this.$set(this.memberCopy, "email", this.member.email);
      this.$set(this.memberCopy, "employeeId", this.member.employeeId);
      this.$set(this.memberCopy, "registerDate", this.member.registeredDate);
      this.formData = {};
      this.disableSave = true;
      this.$validator.reset();
    },

    async resetPassword() {
      this.processingForgotPasswordEmail = true;

      try {
        await this.forgotPassword(this.member.email);
        await this.snackBar({
          type: "is-success",
          message: `A reset password email has been sent to ${this.member.email}.`
        });
      } catch (error) {
        await this.snackBar({
          type: "is-danger",
          message: `There was an error. Please contact wellness@be-engaged.co`
        });
      }

      this.processingForgotPasswordEmail = false;
    },

    deleteMember() {
      const deleteMessage = [
        "<div class='title is-4'>Delete ",
        `${this.member.username}`,
        "</div>",
        "By deleting this member you will <b>PERMANENTLY</b> delete all step",
        "data, journal entries, discussion posts, photos, and profile settings",
        "associated with this member.  This information cannot be recovered",
        "once the member has been delete",
        "<br /><br />",
        "<b>Are you sure you want to delete ",
        `${this.member.username}`,
        "'s account?</b>"
      ].join(" ");

      this.$buefy.dialog.confirm({
        message: deleteMessage,
        confirmText: "Delete Account",
        type: "is-danger",
        onConfirm: async () => {
          this.processingDelete = true;
          const response = await this.delete(this.member.id);
          this.processingDelete = false;

          if (response && response.data.success) {
            await this.snackBar({
              message: `${this.member.username}'s account has been deleted.`
            });

            await this.$router.push({ name: this.backRouteName });
          } else {
            await this.snackBar({
              type: "is-danger",
              duration: 9000,
              message: `There was an error deleting this users account.
                       Please try again. Or please reach out to wellness@be-engaged.co.`
            });
          }
        }
      });
    },

    toggleAdmin() {
      const title = this.member.programAdmin
        ? `Revoke admin privileges`
        : `Make ${this.member.username} an admin`;

      const message = this.member.programAdmin
        ? `Do you wish to revoke all administrative privileges for this member?
        They will no longer be able to perform administrative actions, and will not be alerted.
        `
        : `Do you wish to make this member an administrator of this program?
        They will be able to perform all administrative actions!
        `;

      this.$buefy.dialog.confirm({
        title,
        message,
        confirmText: this.adminButtonText,
        type: "is-primary",
        onConfirm: async () => {
          this.processingAdmin = true;
          const admin = this.member.programAdmin;
          if (admin) {
            await this.revokeAdministrator(admin.id);
          } else {
            await this.makeAdministrator(this.member.id);
          }
          this.processingAdmin = false;
        }
      });
    },

    makeAdministrator(memberId) {
      return this.makeAdmin({ memberId }).then(response => {
        if (response && response.data.success) {
          this.member.programAdmin = response.data.data;
          this.snackBar({
            message: `${this.member.username} is now an administrator.`
          });
          this.$emit("admin-updated");
        }
      });
    },

    revokeAdministrator(programAdminId) {
      return this.revokeAdmin({ programAdminId }).then(response => {
        if (response && response.data.success) {
          this.member.programAdmin = null;
          this.snackBar({
            message: `${this.member.username} is no longer an administrator.`
          });
          this.$emit("admin-updated");
        }
      });
    },
    async changePassword() {
      const validationResult = await this.$validator.validateAll();

      if (!validationResult) return;

      this.changingPassword = true;
      const formData = new FormData();
      formData.append("newPassword", this.newPassword);
      formData.append("newPassword_confirmation", this.confirmPassword);

      try {
        const response = await this.resetMemberPassword({
          memberId: this.member.id,
          formData
        });

        if (response && response.data && response.data.success) {
          await this.snackBar({
            type: "is-success",
            message: "Password has been changed."
          });
          this.newPassword = "";
          this.confirmPassword = "";
          this.$validator.reset();
        }
      } catch (error) {
        const errorMsg = this._.get(error.response, "data.error.fields", {});
        const firstError = Object.values(errorMsg)[0];
        await this.snackBar({
          type: "is-danger",
          message: firstError
            ? firstError[0]
            : "Error changing password. Please try again."
        });
      }

      this.changingPassword = false;
    },

    checkEmailDisableSave(event) {
      if (!event) {
        this.disableSave = true;
      } else if (
        this.memberCopy.email !== this.member.email &&
        this.memberCopy.email !== ""
      ) {
        this.disableSave = false;
        this.formData.email = this.memberCopy.email;
      } else if (this.memberCopy.email === this.member.email) {
        this.disableSave = true;
        delete this.formData.email;
      }
    },

    checkUsernameDisableSave(event) {
      if (!event) {
        this.disableSave = true;
      } else if (
        this.memberCopy.username !== this.member.username &&
        this.memberCopy.username !== ""
      ) {
        this.disableSave = false;
        this.formData.username = this.memberCopy.username;
      } else if (this.memberCopy.username === this.member.username) {
        this.disableSave = true;

        if (this.formData.hasOwnProperty("username")) {
          delete this.formData.username;
        }
      }
    },

    async unlockAccount() {
      this.processingUnlock = true;

      try {
        await this.$axios.post(
          `super/members/${this.member.id}/unlock-account`
        );
        await this.snackBar({
          type: "is-success",
          message: `${this.member.username}'s account has been unlocked.`
        });
        this.$emit("admin-updated");
      } catch (error) {
        await this.snackBar({
          type: "is-danger",
          message: "Error unlocking account. Please try again."
        });
      }

      this.processingUnlock = false;
    }
  }
};
</script>

<style lang="sass">
.reset-password-button
  margin-left: -1.5px
.full-width-dropdown
  width: 100%
  div,button
    width: 100%
  /deep/ button
    display: flex
    justify-content: space-between
</style>
